import React, { useState } from 'react';
import Table from '../../components/Table/Table';
import RoleForm from '../../components/Auth/RoleForm';
import { Role, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole } from '../../hooks/useRoles';
import { useToast } from '../../contexts/ToastContext';

const RolesManagement: React.FC = () => {
  const { showToast } = useToast();
  // Fetch roles from API
  const { data: roles = [], isLoading, error, refetch } = useRoles();

  // Mutations for CRUD operations
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();

  // Local state - must be before early returns
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | undefined>();

  // Loading and error states - NOW we can have early returns
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Error loading roles. Please try again.</div>
      </div>
    );
  }

  const columns: TableColumn[] = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'code', label: 'Code', sortable: true, render: (value: string) => (
      <span className="text-xs font-mono text-gray-600">{value || '-'}</span>
    )},
    { key: 'description', label: 'Description', sortable: false, render: (value: string) => (
      <span className="text-sm text-gray-600 truncate max-w-xs block">{value || '-'}</span>
    )},
    { key: 'usersCount', label: 'Users', sortable: true, render: (value: number) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {value ?? 0}
      </span>
    )},
    { key: 'isSystem', label: 'Type', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {value ? 'System' : 'Custom'}
      </span>
    )},
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'updatedAt', label: 'Updated', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
  ];

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setShowForm(true);
  };

  const handleDelete = async (role: Role) => {
    if (role.isSystem) {
      alert('System roles cannot be deleted.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete role "${role.name}"? This action cannot be undone.`)) {
      await deleteMutation.execute(role);
      refetch();
    }
  };

  const handleAdd = () => {
    setEditingRole(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Role>) => {
    try {
      if (editingRole) {
        await updateMutation.execute({ id: editingRole.id, data });
      } else {
        await createMutation.execute(data);
      }
      showToast(`Role ${editingRole ? 'updated' : 'created'} with ${(data.permissions as any[])?.length || 0} permissions`, 'success');
    } catch (e: any) {
      showToast(e?.message || 'Failed to save role permissions', 'error');
      return;
    }
    setShowForm(false);
    setEditingRole(undefined);
    refetch();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingRole(undefined);
  };

  const handleExport = () => {
    const exportData = roles.map((role: Role) => ({
      'Role Name': role.name,
      'Description': role.description || '',
      'Permissions': role.permissions?.length || 0,
      'Users Count': role.usersCount || 0,
      'Status': role.isActive ? 'Active' : 'Inactive',
      'Type': role.isSystem ? 'System' : 'Custom',
      'Created': role.createdAt ? new Date(role.createdAt).toLocaleDateString() : '-'
    }));
    
    exportToExcel(exportData, `roles_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = roles.map((role: Role) => ({
      'Role Name': role.name,
      'Description': role.description || '',
      'Permissions': role.permissions?.length || 0,
      'Users Count': role.usersCount || 0,
      'Status': role.isActive ? 'Active' : 'Inactive',
      'Type': role.isSystem ? 'System' : 'Custom',
      'Created': role.createdAt ? new Date(role.createdAt).toLocaleDateString() : '-'
    }));
    
    printAsExcel(exportData, 'Roles');
  };

  // Statistics
  const activeRoles = roles.filter((role: Role) => role.isActive).length;
  const systemRoles = roles.filter((role: Role) => role.isSystem).length;
  const customRoles = roles.filter((role: Role) => !role.isSystem).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles Management</h1>
          <p className="text-gray-600">Create and manage user roles with specific module permissions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
          >
            Export
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Print
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Roles</dt>
                  <dd className="text-lg font-medium text-gray-900">{roles.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Roles</dt>
                  <dd className="text-lg font-medium text-gray-900">{activeRoles}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">System Roles</dt>
                  <dd className="text-lg font-medium text-gray-900">{systemRoles}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Custom Roles</dt>
                  <dd className="text-lg font-medium text-gray-900">{customRoles}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Important:</strong> System roles cannot be deleted and have predefined permissions. 
              Custom roles can be modified and deleted. Changes to roles will affect all users assigned to those roles.
            </p>
          </div>
        </div>
      </div>
      
      <Table
        columns={columns}
        data={roles}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="System Roles"
        searchPlaceholder="Search roles..."
      />
      
      {showForm && (
        <RoleForm
          role={editingRole}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default RolesManagement;